import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { ArrowDownward, ArrowUpward } from '@mui/icons-material';

import { PricedSearchResult, ProvidersOutcome } from '../queries';
import { formatMoney } from '../money';
import { useAudience } from '../audience';
import { ESIM_SORT_KEYS, EsimSortKey, sortEsims } from '../esims';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { MetaLine } from './MetaLine';
import { PriceBlock } from './PriceBlock';
import { RuteroResultCard } from './RuteroResultCard';


/** El mismo rótulo que encabeza su columna en la tabla. */
const SORT_LABEL_KEYS: Record<EsimSortKey, string> = {
  price: 'travelServices.finalPrice',
  data: 'travelServices.esims.package',
  validity: 'travelServices.esims.validityCol',
};

interface Props {
  results: PricedSearchResult[];
  providers?: ProvidersOutcome;
  bookingDisabled?: boolean;
  onBook: (result: PricedSearchResult) => void;
}

function EsimPackageFacts({ result }: { result: PricedSearchResult }) {
  const { t } = useTranslation();
  const facts = [
    result.planType ? t('travelServices.esims.planType', { value: result.planType }) : undefined,
    typeof result.voiceMinutes === 'number'
      ? t('travelServices.esims.voiceMinutes', { count: result.voiceMinutes })
      : undefined,
    typeof result.textMessages === 'number'
      ? t('travelServices.esims.textMessages', { count: result.textMessages })
      : undefined,
    result.activationPolicy
      ? t('travelServices.esims.activationPolicy', { value: result.activationPolicy })
      : undefined,
    result.requiresRoaming === true ? t('travelServices.esims.requiresRoaming') : undefined,
    result.apn ? t('travelServices.esims.apn', { value: result.apn }) : undefined,
  ].filter((value): value is string => Boolean(value));
  return facts.length ? (
    <Stack spacing={0.25} mt={0.5}>
      {facts.map((fact) => (
        <Typography key={fact} variant="caption" color="text.secondary">{fact}</Typography>
      ))}
    </Stack>
  ) : null;
}

/**
 * Vista densa de paquetes de eSIM (patrón del portal del proveedor): tabla
 * comparable y ordenable. Los filtros viven en el panel del buscador; acá
 * llegan los resultados ya filtrados.
 */
export function EsimsResultsTable({ results, providers, bookingDisabled, onBook }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { isAgent } = useAudience();
  const [sortKey, setSortKey] = useState<EsimSortKey>('price');
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = useMemo(
    () => sortEsims(results, sortKey, sortAsc),
    [results, sortKey, sortAsc],
  );

  const toggleSort = (key: EsimSortKey) => {
    if (key === sortKey) {
      setSortAsc((prev) => !prev);
      return;
    }
    setSortKey(key);
    setSortAsc(true);
  };

  const sortLabel = (key: EsimSortKey, label: string) => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key && !sortAsc ? 'desc' : 'asc'}
      onClick={() => toggleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <ProviderStatusStrip providers={providers} />

      <Typography variant="body2" color="text.secondary">
        {t('travelServices.esims.available', { count: sorted.length })}
      </Typography>

      {sorted.length === 0 ? (
        <Alert severity="info">{t('travelServices.noResults')}</Alert>
      ) : isMobile ? (
        <>
          {/* Comparar es el punto de esta vertical: el orden tiene que existir
              también donde no hay encabezados de tabla que tocar. */}
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              select
              size="small"
              fullWidth
              label={t('travelServices.cars.sort')}
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value as EsimSortKey)}
            >
              {ESIM_SORT_KEYS.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(SORT_LABEL_KEYS[key])}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title={t('travelServices.sortDirection')}>
              <IconButton
                aria-label={t('travelServices.sortDirection')}
                onClick={() => setSortAsc((previous) => !previous)}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                {sortAsc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* A 375px una tabla de siete columnas es scroll horizontal. Los
              mismos datos se apilan con el chrome de las otras ocho
              verticales: hechos como texto quieto y la fila de cierre abajo. */}
          <Box display="flex" flexDirection="column" gap={1.5}>
            {sorted.map((result, index) => (
              <RuteroResultCard
                key={`${result.provider}-${index}`}
                index={index}
                title={
                  <Typography sx={{ fontSize: 15.5, fontWeight: 600 }}>
                    {result.coverage ?? '—'}
                  </Typography>
                }
                details={
                  <>
                    <MetaLine
                      items={[
                        result.operator,
                        result.unlimited
                          ? t('travelServices.esims.unlimited')
                          : result.dataAmount,
                        typeof result.validityDays === 'number'
                          ? `${result.validityDays} ${t('travelServices.esims.days')}`
                          : undefined,
                      ]}
                    />
                    <EsimPackageFacts result={result} />
                  </>
                }
                price={
                  <PriceBlock
                    pricing={result.pricing}
                    agentNotes={
                      result.retailPrice &&
                    t('travelServices.esims.suggestedPrice', {
                      price: formatMoney(result.retailPrice),
                    })
                    }
                  />
                }
                action={
                  <Button
                    variant="contained"
                    sx={{ minWidth: 104 }}
                    disabled={bookingDisabled}
                    onClick={() => onBook(result)}
                  >
                    {t('travelServices.book')}
                  </Button>
                }
              />
            ))}
          </Box>
        </>
      ) : (
        /* Siete columnas no entran en un viewport de 600-1000px, y lo ancho
           scrollea DENTRO de su contenedor: el body nunca scrollea horizontal.
           Como el contenedor es un scroll container, su ancho mínimo
           automático es 0 y deja de empujar a la página; donde la tabla entra
           entera (desktop ancho) no aparece barra alguna. */
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>
                  {t('travelServices.esims.coverage')}
                </TableCell>
                <TableCell>{t('travelServices.esims.network')}</TableCell>
                <TableCell>
                  {sortLabel('data', t('travelServices.esims.package'))}
                </TableCell>
                <TableCell>
                  {sortLabel('validity', t('travelServices.esims.validityCol'))}
                </TableCell>
                {/* Neto y PVP sugerido son columnas del asesor: en una
                    superficie minorista la tabla no las tiene. */}
                {isAgent && (
                  <>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {t('travelServices.detail.net')}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {t('travelServices.esims.retailCol')}
                    </TableCell>
                  </>
                )}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {sortLabel('price', t('travelServices.finalPrice'))}
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((result, index) => (
                <TableRow key={`${result.provider}-${index}`} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    {result.coverage ?? '—'}
                  </TableCell>
                  <TableCell>{result.operator ?? '—'}</TableCell>
                  <TableCell>
                    {result.unlimited
                      ? t('travelServices.esims.unlimited')
                      : (result.dataAmount ?? '—')}
                    <EsimPackageFacts result={result} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {typeof result.validityDays === 'number'
                      ? `${result.validityDays} ${t('travelServices.esims.days')}`
                      : '—'}
                  </TableCell>
                  {isAgent && (
                    <>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatMoney(result.pricing.net)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {result.retailPrice ? formatMoney(result.retailPrice) : '—'}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(result.pricing.final)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" variant="contained" disabled={bookingDisabled} onClick={() => onBook(result)}>
                      {t('travelServices.book')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
